import React, { useState, useContext, useEffect } from "react";
import { Drawer } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { getIcon } from "../../utils/IconHandler";
import MenuContext from "../../context/MenuContext";
import "./Sidebar.scss";

const Sidebar = ({ isMobile, drawerOpen, onDrawerToggle, isCollapsed, onToggleSidebar }) => {
  const { menu } = useContext(MenuContext);

  const navigate = useNavigate();
  const location = useLocation();

  const [collapsedMenus, setCollapsedMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  useEffect(() => {
    if (isMobile) {
      setCollapsedMenus({});
    }
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const clearHoverCloseTimeout = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const scheduleHoverClose = () => {
    clearHoverCloseTimeout();
    const timeout = setTimeout(() => {
      setHoveredMenu(null);
      setHoverTimeout(null);
    }, 180);
    setHoverTimeout(timeout);
  };

  const toggleCollapseMenu = (menuKey) => {
    setCollapsedMenus((prevState) => ({
      ...prevState,
      [menuKey]: !prevState[menuKey],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onDrawerToggle();
    }
  };

  const isParentActive = (menuItem) => {
    return menuItem.submenu?.some((subItem) => location.pathname === subItem.path || isParentActive(subItem));
  };

  const getMenuKey = (menuItem, parentKey, index) => {
    return `${parentKey}_${menuItem.path || menuItem.title || 'item'}_${index}`;
  };

  // Badge numérico (ej. ítems de conteo de stock pendientes) o un punto simple cuando
  // sabemos que falta algo pero todavía no hay una cantidad exacta (ver MenuItem.badgeDot).
  const renderItemBadge = (menuItem) => {
    if (menuItem.badgeCount) {
      return <span className="menu-badge menu-badge-count">{menuItem.badgeCount}</span>;
    }
    if (menuItem.badgeDot) {
      return <span className="menu-badge menu-badge-dot" aria-hidden="true"></span>;
    }
    return null;
  };

  const renderSubmenu = (submenu, parentKey, depth = 1) => (
    <ul className={`sidebar-submenu expanded depth-${depth}`}>
      {submenu.map((subItem, index) => {
        const itemKey = getMenuKey(subItem, parentKey, index);
        const hasSubmenu = subItem.submenu && subItem.submenu.length > 0;
        const isExpanded = collapsedMenus[itemKey] || isParentActive(subItem);
        const isActive = location.pathname === subItem.path || isParentActive(subItem);

        return (
          <li
            key={itemKey}
            className={`sidebar-submenu-item ${isActive ? "active" : ""} ${hasSubmenu ? "has-submenu" : ""}`}
          >
            {hasSubmenu ? (
              <>
                <div className="sidebar-submenu-title" onClick={() => toggleCollapseMenu(itemKey)}>
                  <span className="menu-text">{subItem.title}</span>
                  <ion-icon
                    className={`arrow-icon ${isExpanded ? "expanded" : ""}`}
                    name={`chevron-${isExpanded ? "down" : "forward"}-outline`}
                  ></ion-icon>
                </div>
                {isExpanded && renderSubmenu(subItem.submenu, itemKey, depth + 1)}
              </>
            ) : (
              <div onClick={() => handleNavigation(subItem.path)}>
                <span className="sidebar-submenu-bullet" aria-hidden="true"></span>
                <span className="menu-text">{subItem.title}</span>
                {renderItemBadge(subItem)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderFloatingMenu = (menuItem, menuKey, depth = 1) => (
    <div
      className={`floating-menu ${hoveredMenu === menuKey ? "visible" : ""} depth-${depth}`}
      onMouseEnter={() => {
        clearHoverCloseTimeout();
        setHoveredMenu(menuKey);
      }}
      onMouseLeave={() => {
        scheduleHoverClose();
      }}
    >
      {menuItem.submenu.map((subItem, index) => {
        const itemKey = getMenuKey(subItem, menuKey, index);
        const hasSubmenu = subItem.submenu && subItem.submenu.length > 0;
        const isExpanded = collapsedMenus[itemKey] || isParentActive(subItem);
        const isActive = location.pathname === subItem.path || isParentActive(subItem);

        return (
          <div key={itemKey} className={`floating-menu-item ${isActive ? "active" : ""} ${hasSubmenu ? "has-submenu" : ""}`}>
            {hasSubmenu ? (
              <div className="floating-menu-title" onClick={() => toggleCollapseMenu(itemKey)}>
                <span className="menu-text">{subItem.title}</span>
                <ion-icon
                  className={`arrow-icon ${isExpanded ? "expanded" : ""}`}
                  name={`chevron-${isExpanded ? "down" : "forward"}-outline`}
                ></ion-icon>
              </div>
            ) : (
              <div onClick={() => handleNavigation(subItem.path)}>
                <span className="sidebar-submenu-bullet" aria-hidden="true"></span>
                <span className="menu-text">{subItem.title}</span>
                {renderItemBadge(subItem)}
              </div>
            )}
            {hasSubmenu && isExpanded && renderFloatingMenu(subItem, itemKey, depth + 1)}
          </div>
        );
      })}
    </div>
  );

  const renderMenuItem = (menuItem, index) => {
    const menuKey = menuItem.path || `${menuItem.title}_${index}`;
    const isExpanded = collapsedMenus[menuKey] || isParentActive(menuItem);
    const hasSubmenu = menuItem.submenu && menuItem.submenu.length > 0;
    const isActive = location.pathname === menuItem.path || isParentActive(menuItem);

    return (
      <li
        key={menuKey}
        className={`sidebar-menu-item ${isActive ? "active" : ""}`}
        onMouseEnter={() => {
          if (isCollapsed) {
            clearHoverCloseTimeout();
            setHoveredMenu(menuKey);
          }
        }}
        onMouseLeave={() => {
          if (isCollapsed) {
            scheduleHoverClose();
          }
        }}
      >
        <div
          className={`sidebar-menu-title ${hasSubmenu ? "has-submenu" : ""}`}
          onClick={() => {
            if (!hasSubmenu) {
              handleNavigation(menuItem.path);
            } else {
              toggleCollapseMenu(menuKey);
            }
          }}
        >
          <span className="menu-icon">{getIcon(menuItem.icon)}</span>
          {!isCollapsed && <span className="menu-text">{menuItem.title}</span>}
          {!isCollapsed && renderItemBadge(menuItem)}
          {!isCollapsed && hasSubmenu && (
            <ion-icon
              className={`arrow-icon ${isExpanded ? "expanded" : ""}`}
              name={`chevron-${isExpanded ? "down" : "forward"}-outline`}
            ></ion-icon>
          )}
        </div>
        {!isCollapsed && hasSubmenu && isExpanded && renderSubmenu(menuItem.submenu, menuKey)}
        {isCollapsed && hasSubmenu && hoveredMenu === menuKey && renderFloatingMenu(menuItem, menuKey)}
      </li>
    );
  };

  return (
    <>
      <Drawer anchor="left" open={isMobile ? drawerOpen : false} onClose={onDrawerToggle}>
        <div className="sidebar sidebar-drawer">
          <div className="sidebar-content">
            <ul className="sidebar-menu">{menu.map((item, index) => renderMenuItem(item, index))}</ul>
          </div>
        </div>
      </Drawer>

      {!isMobile && (
        <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
          <div className="sidebar-content">
            <ul className="sidebar-menu">{menu.map((item, index) => renderMenuItem(item, index))}</ul>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(Sidebar);
